/**
 * The "Admin" menu — managing the studio itself. See
 * lib/navigation/nav-manifest.js for the entry shape and the vocabulary
 * `requires` draws from.
 *
 * Note this menu is never trimmed by a studio's `menuOverride` allow-list (see
 * `overrideExempt` on its manifest entry): it is where a stripped studio is
 * reconfigured from.
 */

/** @type {import("@/lib/navigation/nav-manifest").NavManifestItem[]} */
export const ADMIN_ITEMS = [
  {
    label: "Studio",
    iconName: "HiOutlineCog8Tooth",
    requires: ["workspace", "supervisorOrAbove"],
    subItems: [
      {
        href: "/workspaces/{workspaceId}/edit",
        label: "Studio Settings",
        heading: "Studio settings",
        description:
          "Customize your studio's profile, language, notifications, and connections.",
        iconName: "HiOutlineCog8Tooth",
        requires: "admin",
      },
      {
        href: "/workspaces/{workspaceId}/positioning",
        label: "Positioning",
        description:
          "Define your company's unique positioning. Curate context, generate messaging with AI, and refine before sharing.",
        iconName: "IoFlagOutline",
        requires: "admin",
      },
      {
        href: "/workspaces/admin/data/modules",
        label: "Modules",
        heading: "Modules manager",
        description:
          "Manage your training modules and customize their descriptions.",
        iconName: "LiaLayerGroupSolid",
        requires: "admin",
      },
      {
        href: "/workspaces/admin/phone-practice",
        label: "Phone Practice",
        heading: "Phone practice",
        description:
          "Let users run scored practice sessions over a real phone call. Calls cost per minute, so the feature is off by default.",
        iconName: "IoCallOutline",
        requires: ["admin", "phonePractice"],
      },
      {
        href: "/workspaces/broadcast",
        label: "Send Broadcast",
        heading: "Broadcast Messages",
        description:
          "Send important updates to your team members and keep everyone in sync.",
        iconName: "FaBroadcastTower",
        requires: "admin",
      },
      {
        href: "/help/admin",
        label: "Admin Help",
        heading: "Admin Help Center",
        iconName: "IoHelpCircleOutline",
      },
    ],
  },
  {
    label: "People",
    iconName: "IoPeopleOutline",
    requires: ["workspace", "adminOrSupervisorOrLeader"],
    subItems: [
      {
        href: "/workspaces/admin/people",
        label: "People",
        iconName: "IoPeopleOutline",
      },
      {
        href: "/workspaces/admin/people/communities",
        label: "Communities",
        description: "Create and manage communities to organize your members.",
        iconName: "IoPeopleOutline",
        requires: "supervisorOrAbove",
      },
    ],
  },
  {
    label: "Training",
    iconName: "SlGraduation",
    requires: ["workspace", "adminOrSupervisorOrLeader"],
    subItems: [
      {
        href: "/workspaces/admin/role-plays",
        label: "Assignments",
        description: "Create and manage assignments for your team.",
        iconName: "ClipboardList",
      },
      {
        href: "/workspaces/admin/learning-paths",
        label: "Learning Paths",
        heading: "Learning Path Management",
        description:
          "Build and assign multi-step learning paths for your team.",
        iconName: "SlGraduation",
        requires: "supervisorOrAbove",
      },
      {
        href: "/workspaces/admin/tests",
        label: "Tests",
        heading: "Test management",
        description:
          "Create multiple-choice tests and assign them to your team.",
        iconName: "IoDocumentTextOutline",
      },
      {
        href: "/workspaces/admin/faq-tests",
        label: "Q&A Tests",
        heading: "Q&A test management",
        description: "Create Q&A-based tests and assign them to your team.",
        iconName: "IoHelpCircleOutline",
      },
      {
        href: "/workspaces/admin/glossary-tests",
        label: "Glossary Tests",
        heading: "Glossary test management",
        description:
          "Create glossary-based tests and assign them to your team.",
        iconName: "IoBookOutline",
      },
      {
        href: "/workspaces/admin/fbiv-tests",
        label: "Capability Map Tests",
        heading: "Capability Map test management",
        description:
          "Create Capability Map tests and assign them to your team.",
        iconName: "Target",
      },
    ],
  },
  {
    label: "Content",
    iconName: "IoDocumentTextOutline",
    requires: ["workspace", "admin"],
    subItems: [
      {
        href: "/workspaces/admin/data/content",
        label: "Content Library",
        description:
          "Upload, discover, and organize your studio's learning resources.",
        iconName: "IoDocumentTextOutline",
      },
      {
        href: "/workspaces/admin/glossary",
        label: "Glossary",
        description:
          "Define and organize your studio vocabulary and terminology.",
        iconName: "IoBookOutline",
      },
      {
        href: "/workspaces/admin/faqs",
        label: "Q&As",
        description:
          "Create and organize questions and answers for your studio.",
        iconName: "IoHelpCircleOutline",
      },
      {
        href: "/workspaces/admin/fbiv",
        label: "Capability Maps",
        description:
          "Organize your product features, benefits, impacts, and value themes.",
        iconName: "Target",
      },
      {
        href: "/workspaces/admin/takeaway-reports",
        label: "Takeaway Reports",
        heading: "Takeaway reports",
        description:
          "Capture key insights from simulations and conversations as reusable reports for your team.",
        iconName: "AiOutlineHighlight",
      },
      {
        href: "/workspaces/admin/data/stories",
        label: "Stories",
        heading: "Story builder setup",
        description:
          "Control the questions reps answer when building each story type. Use the standard built-in flow, or write your own custom prompts.",
        iconName: "IoBookOutline",
      },
    ],
  },
  {
    label: "Scenarios",
    iconName: "LiaTheaterMasksSolid",
    requires: ["workspace", "admin"],
    subItems: [
      {
        href: "/workspaces/admin/data/discovery",
        label: "Discovery",
        iconName: "CiMap",
      },
      {
        href: "/workspaces/admin/data/pitch",
        label: "Messaging / Pitch",
        iconName: "HiOutlinePresentationChartBar",
      },
      {
        href: "/workspaces/admin/data/cold-call",
        label: "Outbound & Inbound",
        description: "Manage your Outbound & Inbound scenarios.",
        iconName: "IoCallOutline",
      },
      {
        href: "/workspaces/admin/data/objections",
        label: "Objections",
        iconName: "HiNoSymbol",
      },
      {
        href: "/workspaces/admin/data/plays",
        label: "Plays",
        iconName: "LiaTheaterMasksSolid",
      },
      {
        href: "/workspaces/admin/data/management-scenarios",
        label: "Management",
        heading: "Management scenarios",
        description: "Manage your management practice scenarios.",
        iconName: "IoPersonOutline",
        requires: "management",
      },
      {
        href: "/dem-cog/dems",
        label: "Expert Models (DEMs)",
        heading: "Discovery Expert Models",
        description:
          "Build and refine diagnostic frameworks that capture expert-level discovery methodology.",
        iconName: "ClipboardList",
        requires: "dealClinic",
      },
    ],
  },
  {
    label: "Personas & Scoring",
    iconName: "IoPersonOutline",
    requires: ["workspace", "admin"],
    subItems: [
      {
        href: "/workspaces/admin/data/personas",
        label: "Personas",
        heading: "Buyer personas",
        description: "Manage and organize your buyer personas.",
        iconName: "IoPersonOutline",
      },
      {
        href: "/workspaces/admin/data/seller-personas",
        label: "Other Personas",
        heading: "Other personas",
        description: "Manage seller and other personas for your studio.",
        iconName: "IoPeopleOutline",
        requires: "management",
      },
      {
        href: "/workspaces/admin/data/rubrics",
        label: "Rubrics",
        heading: "All rubrics & scorecards",
        description:
          "Create and manage rubrics and scorecards for your simulations.",
        iconName: "IoListOutline",
      },
    ],
  },
  {
    label: "Performance",
    iconName: "Target",
    requires: ["workspace", "adminOrSupervisorOrLeader"],
    subItems: [
      {
        href: "/workspaces/admin/okrs",
        label: "OKRs",
        heading: "Objectives and key results",
        description: "Set objectives and track key results across your team.",
        iconName: "Target",
      },
      {
        href: "/workspaces/admin/badges",
        label: "Badges",
        description: "Recognize milestones and achievements across your team.",
        iconName: "IoRibbonOutline",
        requires: "badges",
      },
    ],
  },
];
